import { Injectable } from '@angular/core';
import { ProductAnalyticsService, ProductAnalyticsEvent } from './product-analytics.service';
import {
  IRouteRecommendation,
  IRouteRecommendationSearch,
} from 'src/app/interfaces/route-recommendations/route-recommendation';
import { ISetRouteHidden } from '../../interfaces/route-recommendations/route-rejection-reason';

interface TaskRoute {
  taskId: number;
  pickupLocation: string;
  pickupDate: string;
  estPickupHandoverTime: string;
  dropoffLocation: string;
  dropoffDate: string;
  estDropoffHandoverTime: string;
  transportModeSelected: string;
  movementType: string;
  carryover: boolean;
  vehicle: {
    vehicleName: string;
    vehicleMake: string;
    vehicleRegistration: string;
    fuelType: string;
  };
  distance: number;
  estimatedTime: number;
}

@Injectable({ providedIn: 'root' })
export class RoutesAnalyticsService {
  constructor(private productAnalyticsService: ProductAnalyticsService) {}

  private getTaskRoute(route: IRouteRecommendation): TaskRoute[] {
    return route.recommendationTasks.map((t) => {
      return {
        carryover: t.hasCarryover,
        distance: t.movementDistance,
        dropoffDate: t.dropoffEarliestTime,
        dropoffLocation: t.task?.dropoffLocation?.primaryAddress,
        estDropoffHandoverTime: t.dropOffScheduleStartTime,
        estimatedTime: t.estimatedMovementTime,
        estPickupHandoverTime: t.pickupScheduleStartTime,
        movementType: t.task.taskTypeName,
        pickupDate: t.pickupEarliestTime,
        pickupLocation: t.task?.pickupLocation?.primaryAddress,
        taskId: t.task.id,
        transportModeSelected: 'n/a',
        vehicle: {
          fuelType: t?.task?.vehicle?.fuelType,
          vehicleMake: t?.task?.vehicle?.make,
          vehicleName: t?.task?.vehicle?.model,
          vehicleRegistration: t?.task?.vehicle?.registrationNumber,
        },
      } as TaskRoute;
    });
  }

  sendRouteFilterApplyEvent(formType: string, formValue: string) {
    void this.productAnalyticsService.trackEvent(ProductAnalyticsEvent.JobFilterApply, {
      filterType: formType,
      filterValue: formValue,
    });
  }

  sendWatchRouteEvent(routeItem: IRouteRecommendation) {
    void this.productAnalyticsService.trackEvent(ProductAnalyticsEvent.WatchJobRoute, {
      routeId: routeItem.id,
      totalCost: routeItem.totalIncome,
      taskCount: routeItem.taskCount,
      routeTasks: this.getTaskRoute(routeItem),
      driverStartLocation: routeItem.taskToSupplierSegment.destinationName, // Depends on User Input
    });
  }

  sendHideRouteEvent(routeItem: IRouteRecommendation, hideRequest: ISetRouteHidden) {
    void this.productAnalyticsService.trackEvent(ProductAnalyticsEvent.HideJobRoute, {
      routeId: routeItem.id,
      totalCost: routeItem.totalIncome,
      taskCount: routeItem.taskCount,
      reasonId: hideRequest.routeHiddenReasonId,
      reasonComment: hideRequest.routeHiddenComment, // Depends on User Input
    });
  }

  sendRouteSearchEvent(
    searchRequest: IRouteRecommendationSearch,
    inboxNumber: number,
    pageStayDurationSeconds: number,
    isPageExit: boolean = false,
    routeIds: number[] = [],
  ) {
    void this.productAnalyticsService.trackEvent(ProductAnalyticsEvent.RouteSearch, {
      ...searchRequest,
      inboxNumber: inboxNumber,
      pageStayDurationSeconds,
      isPageExit,
      routeIds,
    });
  }

  sendRouteTaskDetailsViewEvent(routeId: number) {
    void this.productAnalyticsService.trackEvent(ProductAnalyticsEvent.RouteTaskDetails, {
      routeId: routeId,
    });
  }

  sendRouteItineraryEvent(routeId: number) {
    void this.productAnalyticsService.trackEvent(ProductAnalyticsEvent.RouteItinerary, {
      routeId: routeId,
    });
  }

  sendTaskSwitchEvent(taskId: number, routeNumber: string) {
    void this.productAnalyticsService.trackEvent(ProductAnalyticsEvent.TaskSwitch, {
      taskId,
      routeNumber,
    });
  }

  sendGoogleDirectionsEvent() {
    void this.productAnalyticsService.trackEvent(ProductAnalyticsEvent.GoogleDirectionsEvent);
  }

  sendGrabNavigationEvent(grabName: string) {
    void this.productAnalyticsService.trackEvent(ProductAnalyticsEvent.GrabNavigationEvent, {
      grabName,
    });
  }

  sendRouteTimingsChangeEvent(primaryRouteId: number, routeId: number) {
    void this.productAnalyticsService.trackEvent(ProductAnalyticsEvent.RouteTimingsChange, {
      primaryRouteId: primaryRouteId,
      routeId: routeId,
    });
  }

  sendItineraryPopupEvent(
    routeId: number,
    popupDurationSeconds: number = 0,
    isPopupClose: boolean = false,
  ) {
    void this.productAnalyticsService.trackEvent(ProductAnalyticsEvent.ItineraryPopup, {
      routeId,
      popupDurationSeconds,
      isPopupClose,
    });
  }

  sendGrabRouteEvent(routeItem: IRouteRecommendation) {
    void this.productAnalyticsService.trackEvent(ProductAnalyticsEvent.GrabRoute, {
      routeId: routeItem.id,
      totalCost: routeItem.totalIncome,
      taskCount: routeItem.taskCount,
      routeTasks: this.getTaskRoute(routeItem),
      driverStartLocation: routeItem.taskToSupplierSegment.destinationName, // Depends on User Input
    });
  }

  sendGrabRouteExitEvent(routeItem: IRouteRecommendation) {
    void this.productAnalyticsService.trackEvent(ProductAnalyticsEvent.GrabRouteModalExit, {
      routeId: routeItem.id,
      totalCost: routeItem.totalIncome,
      referrerPage: 'Route Search - Results', // Route Search - Details
      routeTasks: this.getTaskRoute(routeItem),
    });
  }

  sendRouteDateSelectionEvent(
    routeItem: IRouteRecommendation,
    previousRouteId: number,
    selectedRouteId: number,
    hasCarryOver: boolean,
    optionCount: number,
  ) {
    void this.productAnalyticsService.trackEvent(ProductAnalyticsEvent.RouteDateSelection, {
      representativeRouteId: routeItem.id,
      previousRouteId,
      selectedRouteId,
      hasCarryOver,
      optionCount,
      routeNumber: routeItem.routeNumber,
    });
  }

  sendRouteDetailViewEvent(routeItem: IRouteRecommendation) {
    void this.productAnalyticsService.trackEvent(ProductAnalyticsEvent.RouteDetailsView, {
      routeId: routeItem.id,
      totalCost: routeItem.totalIncome,
      taskCount: routeItem.taskCount,
      routeTasks: this.getTaskRoute(routeItem),
      driverStartLocation: routeItem.taskToSupplierSegment.destinationName, // Depends on User Input
    });
  }
}
